import React, { useEffect, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  InputAdornment,
  Collapse,
  IconButton,
  Paper,
  alpha,
} from '@mui/material';
import { KeyboardArrowDown as KeyboardArrowDownIcon, KeyboardArrowUp as KeyboardArrowUpIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  classifiedConnectorHeaders,
  connectorRows,
  sourceTypeList,
  supportedRegions,
} from '../../Connectors/constants';
import { isConfiguredConnectorVisible } from '../../Connectors/isConnectorVisible';
import { COLORS, FONT_SIZES, FONT_WEIGHTS } from '../../theme/constants';
import api from '../../../Api';
import { ToastNotificationContext } from '../../context/ToastNotificationContext';

const ConnectorTable = ({ setLoading, connectorType }) => {
  const [currentConnectorList, setCurrentConnectorList] = useState({});
  const [hasFetched, setHasFetched] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [expandedRow, setExpandedRow] = useState(null);
  const { setToastNotificationConfig } = useContext(ToastNotificationContext);

  const rows = (connectorRows[connectorType] || []).filter(row =>
    isConfiguredConnectorVisible(connectorType, row.id)
  );

  useEffect(() => {
    const fetchConnectors = async () => {
      try {
        setLoading(true);
        setHasFetched(false);
        const res = await api.getAllConnectors(connectorType);
        const classified = {};
        if (res?.data?.results?.length && sourceTypeList[connectorType]) {
          sourceTypeList[connectorType].forEach(st => {
            classified[st?.dataSource] = res.data.results
              ?.filter(
                connector =>
                  (connector?.data_source_type || '').toUpperCase() ===
                  (st?.dataSource || '').toUpperCase()
              )
              ?.map(conn => ({
                id: conn?.connector_id,
                connector_title: conn?.connector_title,
                connector_name: conn?.connector_name,
                cloud_region: conn?.cloud_region,
                create_time: conn?.create_time
                  ? new Date(conn.create_time).toLocaleString()
                  : '',
                create_user: conn?.create_user
                  ? `${conn.create_user?.firstname || ''} ${conn.create_user?.lastname || ''}`.trim()
                  : '',
                storage_account: conn?.storage_account,
                container: conn?.container,
              }));
          });
        }
        setCurrentConnectorList(classified);
      } catch (err) {
        setToastNotificationConfig({
          open: true,
          kind: 'error',
          title: 'Error Fetching Connectors!',
          subtitle: 'Please try again later.',
        });
      } finally {
        setHasFetched(true);
        setLoading(false);
      }
    };
    fetchConnectors();
  }, [connectorType, setLoading, setToastNotificationConfig]);

  const formatCellValue = (row, key, source) => {
    if (key === 'cloud_region' && supportedRegions[source]) {
      return (
        Object.keys(supportedRegions[source]).find(
          region => supportedRegions[source][region] === row[key]
        ) || row[key]
      );
    }
    return row[key] ?? '';
  };

  const headers = classifiedConnectorHeaders || {};
  const tableHeaders = [{ header: 'Source', key: 'id' }];
  const getSourceLabel = rowId =>
    sourceTypeList[connectorType]?.find(s => (s?.id || s?.dataSource) === rowId)?.title || rowId;

  const searchLower = (searchValue || '').trim().toLowerCase();
  // Hide sources with no configured connectors (e.g. empty REDSHIFT).
  const rowsWithConnectors = !hasFetched
    ? rows
    : rows.filter(row => (currentConnectorList[row.id] || []).length > 0);

  const filteredRows = !searchLower
    ? rowsWithConnectors
    : rowsWithConnectors.filter(row => {
        const label = getSourceLabel(row.id);
        return (
          (row.id || '').toLowerCase().includes(searchLower) ||
          (row.desc || '').toLowerCase().includes(searchLower) ||
          (label || '').toLowerCase().includes(searchLower)
        );
      });

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      // sx={{
      //   border: `1px solid ${COLORS.border.light}`,
      //   borderRadius: 2,
      //   overflow: 'hidden',
      //   maxWidth: '100%',
      //   minWidth: 0,
      //   overflowX: 'auto',
      // }}
    >
      <Box
        sx={{
          px: 2,
          py: 2,
          borderBottom: `1px solid ${COLORS.border.light}`,
          bgcolor: COLORS.background.default,
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Typography
          sx={{
            fontSize: FONT_SIZES.body1,
            fontWeight: FONT_WEIGHTS.medium,
            color: COLORS.text.primary,
          }}
        >
          Configured Connector Details
        </Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Search input text"
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: COLORS.text.hint, fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: COLORS.background.paper,
              fontSize: FONT_SIZES.body2,
              '& fieldset': { borderRadius: 1 },
            },
          }}
        />
      </Box>
      <Table size="medium" aria-label="Connector table" sx={{ '& .MuiTableCell-root': { borderColor: COLORS.border.light } }}>
        <TableHead>
          <TableRow sx={{ bgcolor: COLORS.background.default }}>
            <TableCell padding="checkbox" sx={{ width: 48, py: 1.5, px: 2 }} />
            {tableHeaders.map(h => (
              <TableCell
                key={h.key}
                sx={{
                  fontWeight: FONT_WEIGHTS.semiBold,
                  fontSize: FONT_SIZES.tableHeader,
                  color: COLORS.text.primary,
                  py: 1.5,
                  px: 2.5,
                }}
              >
                {h.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody sx={{ '& tr': { verticalAlign: 'middle' } }}>
          {filteredRows.length === 0 && (
            <TableRow>
              <TableCell colSpan={tableHeaders.length + 1} sx={{ py: 4, textAlign: 'center', color: COLORS.text.secondary, px: 2.5 }}>
                No rows to display.
              </TableCell>
            </TableRow>
          )}
          {filteredRows.map(row => {
            const rowId = row.id;
            const isExpanded = expandedRow === rowId;
            const connectorItems = currentConnectorList[rowId] || [];
            const headCells = headers[rowId] || [];

            return (
              <React.Fragment key={rowId}>
                <TableRow
                  hover
                  sx={{
                    '& > *': { py: 1.5, px: 2.5 },
                    bgcolor: isExpanded ? alpha(COLORS.primary.main, 0.08) : 'transparent',
                  }}
                >
                  <TableCell padding="checkbox" sx={{ width: 48, py: 1.5, px: 2 }}>
                    <IconButton
                      size="small"
                      onClick={() => setExpandedRow(isExpanded ? null : rowId)}
                      aria-expanded={isExpanded}
                      sx={{ color: COLORS.text.secondary }}
                    >
                      {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                    </IconButton>
                  </TableCell>
                  {tableHeaders.map(h => (
                    <TableCell
                      key={h.key}
                      sx={{
                        fontSize: FONT_SIZES.body1,
                        color: COLORS.text.primary,
                        py: 1.5,
                        px: 2.5,
                      }}
                    >
                      {h.key === 'id' ? getSourceLabel(row.id) : row[h.key]}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow sx={{ '& > *': { borderBottom: 'none', py: 0, verticalAlign: 'top' } }}>
                  <TableCell
                    colSpan={tableHeaders.length + 1}
                    padding="none"
                    sx={{ borderBottom: `1px solid ${COLORS.border.light}`, p: 0 }}
                  >
                    <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                      <Box sx={{ py: 2, px: 2.5, bgcolor: COLORS.background.default }}>
                        {connectorItems.length > 0 ? (
                          <Table size="medium" aria-label={`${rowId} connectors`} sx={{ '& .MuiTableCell-root': { borderColor: COLORS.border.light } }}>
                            <TableHead>
                              <TableRow sx={{ bgcolor: COLORS.background.paper }}>
                                {headCells.map(header => (
                                  <TableCell
                                    key={header.key}
                                    sx={{
                                      fontWeight: FONT_WEIGHTS.semiBold,
                                      fontSize: FONT_SIZES.tableHeader,
                                      color: COLORS.text.primary,
                                      py: 1.25,
                                      px: 2,
                                    }}
                                  >
                                    {header.header}
                                  </TableCell>
                                ))}
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {connectorItems.map(item => (
                                <TableRow key={item.id} hover sx={{ '& > *': { py: 1.25, px: 2 } }}>
                                  {headCells.map(header => (
                                    <TableCell
                                      key={`${header.key}-${item.id}`}
                                      sx={{
                                        fontSize: FONT_SIZES.body2,
                                        color: COLORS.text.primary,
                                        py: 1.25,
                                        px: 2,
                                      }}
                                    >
                                      {formatCellValue(item, header.key, rowId)}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        ) : (
                          <Typography
                            sx={{
                              fontSize: FONT_SIZES.body2,
                              color: COLORS.text.secondary,
                              py: 2,
                              px: 0.5,
                            }}
                          >
                            No connectors configured for this source yet.
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

ConnectorTable.propTypes = {
  setLoading: PropTypes.func.isRequired,
  connectorType: PropTypes.string.isRequired,
};

export default ConnectorTable;
